import PolicyLayout, { Section } from "@/components/PolicyLayout";
import { BUSINESS } from "@/data/business";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { RefreshCcw, Truck, ShieldCheck, Mail } from "lucide-react";

export default function ReturnPolicy() {
  return (
    <PolicyLayout
      eyebrow="Our promise"
      title="30-Day Tail-Wag Guarantee"
      intro="We want every dog to fall head-over-paws for their rugby ball. If yours doesn't, we'll make it right — no awkward questions, no runaround."
      testId="return-policy-page"
    >
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 -mt-2">
        {[
          { icon: RefreshCcw, label: "30-day returns" },
          { icon: Truck, label: "Free return shipping (US)" },
          { icon: ShieldCheck, label: "Full refund or replacement" },
        ].map(({ icon: Icon, label }) => (
          <Card key={label} className="rounded-2xl border-border p-5 flex flex-col items-center text-center gap-2">
            <Icon className="text-primary" size={22} strokeWidth={2.5} />
            <span className="text-sm font-semibold">{label}</span>
          </Card>
        ))}
      </div>

      <Section title="What's covered">
        <p>
          If your dog isn't crazy about the ball, or if the toy shows any manufacturing defect (a
          torn squeaker, cracking TPR, off-gassing odor, color bleed), you can return it within{" "}
          <strong>30 days of delivery</strong> for a full refund or a free replacement — your call.
        </p>
      </Section>

      <Section title="How to start a return">
        <ol className="list-decimal list-outside pl-5 space-y-2 marker:text-primary marker:font-bold">
          <li>Email us at <a href={`mailto:${BUSINESS.email.returns}`} className="text-primary font-semibold">{BUSINESS.email.returns}</a> with your order number and a short note about what happened.</li>
          <li>We'll reply within 1 business day with a prepaid return label (US customers) or instructions for international returns.</li>
          <li>Pop the toy in any padded mailer or its original box and drop it off at any USPS location.</li>
          <li>Once we receive the package, your refund hits the original payment method within 3–5 business days.</li>
        </ol>
      </Section>

      <Section title="Who pays return shipping">
        <p>
          <strong>US customers:</strong> we cover return shipping — you'll get a prepaid label.
          <strong> International customers:</strong> return shipping is your responsibility, but
          your refund covers the full product price. Email us first; we'll often issue a partial
          refund and skip the return shipping entirely if it works out cheaper for everyone.
        </p>
      </Section>

      <Section title="Replacements">
        <p>
          Prefer a fresh ball or a different color/size? Just say the word in your email — we'll
          ship a replacement before the return is even back at our warehouse. Once you drop the
          old one off, you're done.
        </p>
      </Section>

      <Section title="Condition of returned items">
        <p>To qualify for a refund or replacement, the returned item should be:</p>
        <ul className="list-disc list-outside pl-5 space-y-2 marker:text-primary">
          <li>Returned within <strong>30 days of delivery</strong></li>
          <li>The original product you ordered (we can't accept other brands)</li>
          <li>Not damaged beyond normal play — see exclusions below</li>
          <li>Rinsed with mild soap and dried (a hygiene courtesy, not a strict requirement)</li>
        </ul>
      </Section>

      <Section title="What we can't accept">
        <ul className="list-disc list-outside pl-5 space-y-2 marker:text-primary">
          <li>Toys returned more than 30 days after delivery.</li>
          <li>Damage caused by inappropriate use — e.g., a heavy chewer breed using a Small ball, machine washing, or being chewed by anything that isn't a dog (cats, kids, lawnmowers — yes, all have happened).</li>
          <li>Items lost in return shipping without a tracked label.</li>
        </ul>
      </Section>

      <Section title="Damaged in transit">
        <p>
          If the package arrives damaged, send us a photo within 48 hours and we'll send a
          replacement that same day — no return required.
        </p>
      </Section>

      <Separator />

      <div className="rounded-2xl bg-primary/5 border border-primary/15 p-5 flex items-start gap-4">
        <Mail className="text-primary mt-0.5 shrink-0" size={20} strokeWidth={2.5} />
        <div className="text-sm leading-relaxed">
          <p className="font-display font-semibold text-base text-foreground">Need help?</p>
          <p className="text-muted-foreground">
            Email{" "}
            <a href={`mailto:${BUSINESS.email.returns}`} className="text-primary font-semibold">{BUSINESS.email.returns}</a>
            {" "}— a real human responds within one business day, usually faster.
          </p>
        </div>
      </div>
    </PolicyLayout>
  );
}
